/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, sonarjs/cognitive-complexity */
import { ColDef, ColGroupDef } from "ag-grid-community";

import { RfpCoverage } from "../../../api/types";
import { BenefitDefinition } from "../../../benefits/benefit.types";
import { hasCurrentPlans } from "../../../locs/requiredFields/validation";
import { ICellDef, ITableRowData } from "../types/rowdata.types";

import type { BenefitValue, CellValue } from "../types/values.types";

// example: { identifier: "DEN-1-1.inNetwork", comment: "Some text"}
export interface NeedsReview {
  identifier: string;
  comment?: string;
}

/*
  TODO: this should not be so strictly defined, benefit, benefitKey are to direct. 
  consider the following to aggregate data rows more  on build. 
  reference: src/components/PlanDesigner/types/rowdata.types.ts
*/

// temp extend of ITableRowData so that i can update without breaking to much
export interface DataRow extends ITableRowData {
  benefit: BenefitDefinition;
  benefitKey: string;
  [key: string]: any; // For plan values
}

/**
 * Processes coverages array and returns coverage where type property matches
 *
 * @param type - Coverage type identifier to search for
 * @param data - Data object containing coverages array
 * @returns Found coverage or undefined
 */
export const getCoverageByType = (
  type: string,
  data: { coverages: RfpCoverage[] },
): RfpCoverage | undefined => {
  if (!data?.coverages || !Array.isArray(data.coverages)) {
    return undefined;
  }

  return data.coverages.find((coverage: RfpCoverage) => coverage.type === type);
};

/**
 * Resolves the effective default for a benefit, honoring cell variants.
 *
 * When a field's cell depends on a sibling "type" field (cellVariants), the
 * selected variant may declare its own defaultValue — e.g. a graded commission
 * amount defaults to null so the amount can stay blank ("no defined
 * percentage"). That variant default takes precedence over the benefit's own
 * defaultValue. Any field without cellVariants, or whose selected variant
 * declares no default, falls back to benefitDef.defaultValue unchanged.
 */
const resolveDefaultValue = (
  benefitDef: BenefitDefinition,
  benefits?: Record<string, unknown>,
): unknown => {
  const cellVariants = benefitDef.cellVariants;
  if (cellVariants && benefits) {
    const typeValue = benefits[cellVariants.dependsOn];
    const variant =
      cellVariants.options[String(typeValue)] ?? cellVariants.default;
    if (variant?.defaultValue !== undefined) {
      return variant.defaultValue;
    }
  }
  return benefitDef.defaultValue;
};

/**
 * Gets value with default fallback
 *
 * Returns the extracted value if it exists and is not empty,
 * otherwise returns the default value from the benefit definition.
 *
 * @param extractedValue - The extracted value from data
 * @param benefitDef - Benefit definition containing default value
 * @param suppressDefault - When true, skip the definition's defaultValue and
 *   treat a missing value as no-data (placeholder plans)
 * @param benefits - Sibling benefit values, used to resolve variant defaults
 * @returns The final value to use (extracted or default)
 */
const getValueWithDefault = (
  extractedValue: CellValue,
  benefitDef: BenefitDefinition,
  suppressDefault = false,
  benefits?: Record<string, unknown>,
): CellValue => {
  // If we have a valid extracted value, use it
  if (
    extractedValue !== "" &&
    extractedValue !== null &&
    extractedValue !== undefined
  ) {
    return extractedValue;
  }

  // Otherwise, return the resolved default (variant-aware) if defined
  if (!suppressDefault) {
    const resolved = resolveDefaultValue(benefitDef, benefits);
    if (resolved !== undefined) {
      return resolved as CellValue;
    }
  }

  // For array-type fields (e.g., eligibilityClasses), default to empty array
  // Using "" for array fields causes AG Grid to auto-detect the cell data type
  // as 'text', which coerces array values from editors to strings
  if (benefitDef.cell?.["dataType"] === "array") {
    return [] as unknown as CellValue;
  }

  // No default, return empty string
  return "";
};

/**
 * Extracts the actual value from a benefit value
 * Handles different data types: string, number, boolean, arrays, or object with amount/age/value keys
 *
 * @param benefitValue - The raw benefit value
 * @param propertyKey - Optional specific property key to extract from the value
 * @returns The extracted value or empty string if not found
 */
const extractBenefitValue = (
  benefitValue: unknown,
  propertyKey?: string,
): CellValue => {
  // Handle null/undefined
  if (benefitValue == null) {
    return "";
  }

  // Handle primitives (string, number, boolean)
  if (typeof benefitValue !== "object") {
    return benefitValue as CellValue;
  }

  // Handle arrays - return them directly for multi-select fields
  if (Array.isArray(benefitValue)) {
    return benefitValue as CellValue;
  }

  // Handle objects - check for specific property key first if provided
  if (typeof benefitValue === "object") {
    const obj = benefitValue as BenefitValue;

    // If a specific property key is requested, try to extract that first
    if (propertyKey && obj[propertyKey] !== undefined) {
      return obj[propertyKey] as CellValue;
    }

    // Fallback to standard extraction logic
    if ("amount" in obj && obj.amount !== undefined) {
      return obj.amount as CellValue;
    }
    if ("age" in obj && obj.age !== undefined) {
      return obj.age as CellValue;
    }
    if ("value" in obj && obj.value !== undefined) {
      return obj.value as CellValue;
    }

    // If none of the expected keys are found, return the object as string
    return JSON.stringify(obj);
  }

  return "";
};

/**
 * Extracts field identifiers from column definitions
 *
 * @param columnDefs - AG Grid column definitions
 * @returns Array of field identifiers
 */
const extractFieldIdentifiers = (
  columnDefs: (ColDef | ColGroupDef)[],
): string[] => {
  const fields: string[] = [];

  const extractFields = (cols: (ColDef | ColGroupDef)[]): void => {
    cols.forEach((col) => {
      if ("children" in col && col.children) {
        // ColGroupDef with children
        extractFields(col.children);
      } else if ("field" in col && col.field) {
        // ColDef with field
        fields.push(col.field);
      }
    });
  };

  extractFields(columnDefs);
  return fields.filter((field) => field !== "benefit.label"); // Exclude benefit label field
};

/**
 * Builds plan values for a specific benefit across all benefit plans
 *
 * @param benefitKey - The key of the benefit to build values for
 * @param coverage - Coverage data containing benefit plans
 * @param fieldIdentifiers - Field identifiers from column definitions
 * @param benefitDef - Benefit definition to check network dependency
 * @param propertyKey - Optional property key to extract specific values
 * @returns Object with plan values keyed by identifier
 */
const buildPlanValuesForBenefit = (
  benefitKey: string,
  coverage: RfpCoverage,
  fieldIdentifiers: string[],
  benefitDef: BenefitDefinition,
  propertyKey?: string,
): Record<string, CellValue | Record<string, CellValue>> => {
  const planValues: Record<string, CellValue | Record<string, CellValue>> = {};

  // Group field identifiers by plan identifier
  const planGroups = new Map<string, string[]>();

  fieldIdentifiers.forEach((fieldId) => {
    const planIdentifier = fieldId.split(".")[0];
    if (!planGroups.has(planIdentifier)) {
      planGroups.set(planIdentifier, []);
    }
    planGroups.get(planIdentifier)!.push(fieldId);
  });

  // Build values for each plan identifier
  planGroups.forEach((fields, planIdentifier) => {
    const benefitPlan = coverage.benefitPlans?.find(
      (plan) =>
        plan.id === planIdentifier || plan.identifier === planIdentifier,
    );

    if (benefitPlan) {
      // Placeholder plans are synthesized "awaiting quote" slots with no real
      // data — never seed them with benefit-definition defaults (e.g. the
      // FE-only "yes" toggles); an empty value renders as no-data instead.
      const suppressDefault = benefitPlan.isPlaceholder === true;

      // Special handling for coverage-level fields (e.g., eligibilityClasses)
      if (benefitDef.coverageLevel === true) {
        const rawCoverageValue = (coverage as any)[benefitKey];
        const coverageValue = extractBenefitValue(
          rawCoverageValue,
          propertyKey,
        );
        planValues[planIdentifier] = coverageValue;
        return;
      }

      // Special handling for benefitPlan-level fields (e.g., currentCarrierName)
      if (benefitDef.benefitPlanLevel === true) {
        const rawBenefitValue = (benefitPlan as any)[benefitKey];
        const extractedValue = extractBenefitValue(
          rawBenefitValue,
          propertyKey,
        );
        const benefitValue = getValueWithDefault(
          extractedValue,
          benefitDef,
          suppressDefault,
        );
        planValues[planIdentifier] = benefitValue;
        return;
      }

      if (benefitPlan.benefits) {
        if (benefitDef.networkDependent === true) {
          // Network dependent: check networkBenefits array
          const networkValues: Record<string, any> = {};
          const benefits = benefitPlan.benefits as any;

          if (
            benefits.networkBenefits &&
            Array.isArray(benefits.networkBenefits)
          ) {
            // Extract values from networkBenefits array
            benefits.networkBenefits.forEach((networkBenefit: any) => {
              if (networkBenefit.network) {
                const rawValue = networkBenefit[benefitKey];
                const extractedValue = extractBenefitValue(
                  rawValue,
                  propertyKey,
                );
                const finalValue = getValueWithDefault(
                  extractedValue,
                  benefitDef,
                  suppressDefault,
                );
                networkValues[networkBenefit.network] = finalValue;
              }
            });
          }

          planValues[planIdentifier] = networkValues as Record<
            string,
            CellValue
          >;
        } else {
          // Not network dependent: use direct value from first level

          const rawBenefitValue = (benefitPlan.benefits as any)[benefitKey];
          const extractedValue = extractBenefitValue(
            rawBenefitValue,
            propertyKey,
          );
          const benefitValue = getValueWithDefault(
            extractedValue,
            benefitDef,
            suppressDefault,
            benefitPlan.benefits as Record<string, unknown>,
          );
          planValues[planIdentifier] = benefitValue;
        }
      } else {
        // No benefits object - use default value if available
        const defaultValue = getValueWithDefault(
          "",
          benefitDef,
          suppressDefault,
        );

        if (benefitDef.networkDependent === true) {
          const networkValues: Record<string, any> = {};
          fields.forEach((fieldId) => {
            const parts = fieldId.split(".");
            const networkKey = parts[parts.length - 1];
            networkValues[networkKey] = defaultValue;
          });
          planValues[planIdentifier] = networkValues;
        } else {
          planValues[planIdentifier] = defaultValue;
        }
      }
    } else {
      // No benefit plan found - use default value if available
      const defaultValue =
        benefitDef.defaultValue !== undefined ? benefitDef.defaultValue : "";

      if (benefitDef.networkDependent === true) {
        const networkValues: Record<string, any> = {};
        fields.forEach((fieldId) => {
          const parts = fieldId.split(".");
          const networkKey = parts[parts.length - 1];
          networkValues[networkKey] = defaultValue;
        });
        planValues[planIdentifier] = networkValues;
      } else {
        planValues[planIdentifier] = defaultValue as CellValue;
      }
    }
  });

  return planValues;
};

/**
 * Builds data rows for AG Grid based on coverage data and benefit definitions
 *
 * @param coverage - Complete coverage data structure
 * @param benefitDefinitions - Object containing benefit definitions keyed by benefit key
 * @param columnDefs - AG Grid column definitions to extract field identifiers
 * @returns Array of data rows for AG Grid
 */
export const buildDataRows = (
  coverage: RfpCoverage,
  benefitDefinitions: Record<string, BenefitDefinition>,
  columnDefs: (ColDef | ColGroupDef)[],
  // When the Current Carrier - Policy feature is on, the per-plan "Current
  // Carrier" row is removed; the policy is selected at the group level instead
  // (ADR 10.22).
  hideCurrentCarrierRow = false,
  // MP configuration field keys the broker's configuration *shows* for this
  // coverage (CD-386). A configuration lists only the fields it shows — a field
  // it hides is stored false or left out entirely — so visibility is expressed
  // as the shown set rather than a hidden one.
  //
  // Opt-in: `undefined` means no configuration applies and every row renders,
  // which is what the Build tab does. Rows with no `mpConfigKey` are never
  // filtered, so an unmapped row always renders.
  visibleMpConfigKeys?: ReadonlySet<string>,
): DataRow[] => {
  // Step 1: Validate inputs
  if (!coverage?.benefitPlans) {
    return [];
  }

  if (!benefitDefinitions || typeof benefitDefinitions !== "object") {
    return [];
  }

  if (!columnDefs || !Array.isArray(columnDefs)) {
    return [];
  }

  // Step 2: Extract field identifiers from column definitions
  const fieldIdentifiers = extractFieldIdentifiers(columnDefs);

  // Step 2.5: Determine if there are any current plans (with policyPeriod "current")
  // If there are NO current plans, exclude "Current Carrier" and "Current Commissions" rows since they're not applicable
  const coverageHasCurrentPlans = hasCurrentPlans(coverage.benefitPlans);
  const currentCoverageExcludedKeys = new Set([
    "currentCarrierName",
    "currentCommissionType",
    "currentCommissionAmount",
  ]);

  // The Incumbent/Requested column is locked to the leftmost plan column, so its
  // benefits solely decide visibility of rows gated by requestedPlanRowVisibility;
  // the carrier-proposed columns to its right are ignored.
  const requestedPlanIdentifier = fieldIdentifiers[0]?.split(".")[0];
  const requestedPlanBenefits = coverage.benefitPlans.find(
    (plan) =>
      plan.id === requestedPlanIdentifier ||
      plan.identifier === requestedPlanIdentifier,
  )?.benefits as Record<string, unknown> | undefined;
  const isRowHiddenByRequestedPlan = (
    benefitDef: BenefitDefinition,
  ): boolean => {
    const gate = benefitDef.requestedPlanRowVisibility;
    if (!gate) {
      return false;
    }
    return requestedPlanBenefits?.[gate.field] !== gate.equals;
  };

  // Step 3: Find benefits that exist in coverage and build rows only for those
  const rows: DataRow[] = [];

  // Build a map to track which benefits have nested fields following them
  const benefitsWithNestedFields = new Map<string, string[]>();
  Object.entries(benefitDefinitions).forEach(([benefitKey, benefitDef]) => {
    if (benefitDef.nested === true) {
      // This is a nested field, find its parent
      // Parent is the previous non-nested benefit in the definitions
      const keys = Object.keys(benefitDefinitions);
      const currentIndex = keys.indexOf(benefitKey);
      for (let i = currentIndex - 1; i >= 0; i--) {
        const potentialParentKey = keys[i];
        if (!benefitDefinitions[potentialParentKey].nested) {
          if (!benefitsWithNestedFields.has(potentialParentKey)) {
            benefitsWithNestedFields.set(potentialParentKey, []);
          }
          benefitsWithNestedFields.get(potentialParentKey)!.push(benefitKey);
          break;
        }
      }
    }
  });

  // Build rows for all benefits defined in benefit definitions
  // No longer filter by what exists in coverage - always show all defined benefits
  Object.entries(benefitDefinitions).forEach(([benefitKey, benefitDef]) => {
    // Skip nested fields here - they'll be added with their parent
    if (benefitDef.nested === true) {
      return;
    }
    // Remove the per-plan "Current Carrier" row when the policy feature is on
    // (the carrier/policy is chosen at the group level instead — ADR 10.22).
    if (hideCurrentCarrierRow && benefitKey === "currentCarrierName") {
      return;
    }
    // Skip "Current Carrier" and "Current Commissions" rows when there are no current plans
    if (
      !coverageHasCurrentPlans &&
      currentCoverageExcludedKeys.has(benefitKey)
    ) {
      return;
    }
    // Hide the whole row when the RFP-requested plan gates it out (e.g. STD/LTD
    // Participation Requirements only shows when requested contribution is voluntary).
    if (isRowHiddenByRequestedPlan(benefitDef)) {
      return;
    }
    // MP-only rows exist for the side-by-side to mirror the presentation; with
    // no configuration in play (the Build tab) they have no business rendering.
    if (benefitDef.mpOnly && !visibleMpConfigKeys) {
      return;
    }
    // Hide rows the broker's MP configuration turns off, so the side-by-side
    // shows what their presentation will.
    if (
      visibleMpConfigKeys &&
      benefitDef.mpConfigKey &&
      !visibleMpConfigKeys.has(benefitDef.mpConfigKey)
    ) {
      return;
    }
    // Check if benefitDef has properties object
    if (benefitDef.properties && typeof benefitDef.properties === "object") {
      // ALWAYS create a parent/header row for the benefit itself when it has properties
      // This shows the benefit key as a label in the grid
      // logger.info(
      //   `[buildRowData] Creating parent row for benefitKey: ${benefitKey}`,
      // );

      const parentPlanValues = buildPlanValuesForBenefit(
        benefitKey,
        coverage,
        fieldIdentifiers,
        benefitDef,
      );

      // Create a modified benefit definition that shows the benefit key as the label
      const parentBenefitDef = {
        ...benefitDef,
        label: benefitKey, // Use the actual benefit key (e.g., "hospitalAdmission")
        cell: benefitDef.cell || { editable: false }, // Use existing cell or read-only default
      };

      const parentRow: DataRow = {
        benefit: parentBenefitDef,
        benefitKey,
        _coverage: coverage, // Store coverage reference for dynamic cell configurations
        ...parentPlanValues,
      };

      // logger.info(`[buildRowData] Parent row created:`, {
      //   benefitKey,
      //   label: parentRow.benefit.label,
      // });
      rows.push(parentRow);

      // Then create a row for each property key
      Object.keys(benefitDef.properties).forEach((propertyKey) => {
        // Build plan values for this benefit and property key
        const planValues = buildPlanValuesForBenefit(
          benefitKey,
          coverage,
          fieldIdentifiers,
          benefitDef,
          propertyKey,
        );

        // Create a modified benefit definition for this property
        const propertyBenefitDef = {
          ...benefitDef,
          label:
            benefitDef.properties?.[propertyKey]?.label ||
            `${benefitDef.label} - ${propertyKey}`,
          description:
            benefitDef.properties?.[propertyKey]?.description ||
            benefitDef.description,
          cell: benefitDef.properties?.[propertyKey]?.cell || benefitDef.cell,
        };

        // Create the row structure for this property
        const row: DataRow = {
          benefit: propertyBenefitDef,
          benefitKey: `${benefitKey}.${propertyKey}`, // Include property key in benefitKey
          propertyKey,
          _coverage: coverage, // Store coverage reference for dynamic cell configurations
          ...planValues,
        };

        rows.push(row);
      });
    } else {
      // No properties object - create single row as before
      const planValues = buildPlanValuesForBenefit(
        benefitKey,
        coverage,
        fieldIdentifiers,
        benefitDef,
      );

      // Create the row structure

      // TODO: UPdate to use the new interface setup in
      // src/components/PlanDesigner/types/rowdata.types.ts
      const row: DataRow = {
        // adding new interface values
        id: "",
        parentId: "",
        groupKey: "",
        meta: {
          cellDef: benefitDef.cell as ICellDef,
          fields: [],
          benefit: {
            ...benefitDef,
            id: benefitKey,
          },
        },
        history: [],
        values: { ...planValues },
        // end interface stuff
        rowPinned: true,
        benefit: benefitDef,
        benefitKey,
        _coverage: coverage, // Store coverage reference for dynamic cell configurations
        ...planValues,
      };

      rows.push(row);
    }

    // Add nested fields that follow this parent benefit
    const nestedFields = benefitsWithNestedFields.get(benefitKey);
    if (nestedFields && nestedFields.length > 0) {
      nestedFields.forEach((nestedKey) => {
        if (hideCurrentCarrierRow && nestedKey === "currentCarrierName") {
          return;
        }
        // Skip excluded nested fields when there are no current plans
        if (
          !coverageHasCurrentPlans &&
          currentCoverageExcludedKeys.has(nestedKey)
        ) {
          return;
        }
        const nestedDef = benefitDefinitions[nestedKey];
        if (nestedDef) {
          const nestedPlanValues = buildPlanValuesForBenefit(
            nestedKey,
            coverage,
            fieldIdentifiers,
            nestedDef,
          );

          const nestedRow: DataRow = {
            benefit: nestedDef,
            benefitKey: nestedKey,
            _coverage: coverage, // Store coverage reference for dynamic cell configurations
            ...nestedPlanValues,
          };

          rows.push(nestedRow);
        }
      });
    }
  });
  // logger.info(rows)
  return rows;
};
